import React, { useState, useRef, useEffect } from 'react';
import { Modal, Stack, Text, IconButton, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';

interface IPreisWriteBackModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  updates: Array<{ bcNummer: string; preis: number }>;
}

export const PreisWriteBackModal: React.FC<IPreisWriteBackModalProps> = ({ isOpen, onDismiss, updates }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLogs([]);
      setDone(false);
      setError(null);
      setIsRunning(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
    };
  }, [isOpen]);

  const start = () => {
    setIsRunning(true);
    setLogs([`Starte Write-Back für ${updates.length} Artikel...`]);

    // POST via fetch mit ReadableStream (EventSource unterstützt kein POST)
    const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
    fetch(`${baseUrl}/api/sync/artikel-preise-bc?stream=true`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
      credentials: 'include',
      body: JSON.stringify({ updates }),
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              const ts = new Date().toLocaleTimeString();
              if (data.message) {
                setLogs(prev => [...prev, `[${ts}] ${data.message}`]);
              }
              if (data.type === 'complete') {
                setLogs(prev => [...prev, `[${ts}] ✓ Abgeschlossen: ${data.updated} Preise gesetzt, ${data.errors} Fehler`]);
                setDone(true);
                setIsRunning(false);
              } else if (data.type === 'error') {
                setError(data.message || 'Unbekannter Fehler');
                setIsRunning(false);
              }
            } catch { /* parse error ignorieren */ }
          }
        }
      }
      setIsRunning(false);
      setDone(true);
    }).catch(err => {
      setError(err.message);
      setIsRunning(false);
    });
  };

  const handleDismiss = () => {
    if (!isRunning) onDismiss();
  };

  return (
    <Modal isOpen={isOpen} onDismiss={handleDismiss} isBlocking={isRunning} containerClassName="sync-modal">
      <Stack styles={{ root: { padding: 24, minWidth: 560, maxWidth: 700 } }} tokens={{ childrenGap: 16 }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>Preise → Business Central</Text>
          <IconButton iconProps={{ iconName: 'Cancel' }} ariaLabel="Schließen" onClick={handleDismiss} disabled={isRunning} />
        </Stack>

        <Text style={{ color: '#605e5c' }}>
          {updates.length} Artikel-Einstandspreise werden in BC aktualisiert. Dies öffnet einen Browser (Puppeteer) und kann mehrere Minuten dauern.
        </Text>

        {error && <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>{error}</MessageBar>}

        {!isRunning && !done && (
          <MessageBar messageBarType={MessageBarType.warning}>
            Achtung: Dieser Vorgang schreibt direkt in Business Central. Bitte prüfen Sie die Abweichungen vorher.
          </MessageBar>
        )}

        {!isRunning && !done && (
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <button
              onClick={start}
              style={{ padding: '10px 20px', fontSize: 15, backgroundColor: '#d83b01', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              Jetzt {updates.length} Preise in BC schreiben
            </button>
            <button onClick={handleDismiss} style={{ padding: '10px 20px', fontSize: 15, backgroundColor: '#f3f2f1', color: '#323130', border: '1px solid #8a8886', borderRadius: 4, cursor: 'pointer' }}>
              Abbrechen
            </button>
          </Stack>
        )}

        {isRunning && (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Spinner size={SpinnerSize.small} />
            <Text>Write-Back läuft... (Browser-Automatisierung aktiv)</Text>
          </Stack>
        )}

        {done && <MessageBar messageBarType={MessageBarType.success}>Write-Back abgeschlossen.</MessageBar>}

        {logs.length > 0 && (
          <Stack
            styles={{ root: { maxHeight: 300, overflowY: 'auto', padding: 12, backgroundColor: '#f3f2f1', borderRadius: 4, fontFamily: 'Consolas, monospace', fontSize: 12 } }}
            tokens={{ childrenGap: 3 }}
          >
            {logs.map((log, i) => <Text key={i} styles={{ root: { fontSize: 12 } }}>{log}</Text>)}
          </Stack>
        )}

        {!isRunning && (done || error) && (
          <Stack horizontal horizontalAlign="end">
            <button onClick={handleDismiss} style={{ padding: '8px 16px', fontSize: 14, backgroundColor: '#f3f2f1', color: '#323130', border: '1px solid #8a8886', borderRadius: 4, cursor: 'pointer' }}>
              Schließen
            </button>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
